import {
  InsurerFormDefinition,
  type FormFieldInput,
  type FormTestInput,
  type InsurerFormInput,
  type PdfTarget,
} from '@cyberscore/contracts';
import { OP_LABEL } from './conditions';
import type { Issue } from './editors';

/** The insurer-form editor's plain logic (task 96): checks, labels and the shapes each choice starts from. */

export type FormDraft = InsurerFormInput;
export type FieldDraft = FormFieldInput;
export type SourceDraft = FieldDraft['source'] extends infer S ? (S extends readonly (infer One)[] ? One : S) : never; // prettier-ignore
export type Scalar = string | number | boolean;

/** Where a field's answer comes from, in the editor's order. The first four are tests (a yes/no field). */
export const SOURCE_KINDS = [
  ['value', 'Copy an answer'],
  ['context', 'A company detail'],
  ['control', 'Controls in place (yes/no)'],
  ['answer', 'An answer test (yes/no)'],
  ['detail', 'A company detail test (yes/no)'],
  ['all', 'All of these tests (yes/no)'],
  ['any', 'Any of these tests (yes/no)'],
  ['not', 'Not this test (yes/no)'],
  ['checklist', 'Each option’s own test (checklist)'],
  ['declaration', 'The director declares it'],
  ['broker', 'The broker fills it'],
  ['not_applicable', 'Doesn’t apply'],
  ['unmapped', 'Not mapped yet (stays on paper)'],
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number][0];
export const TEST_KINDS = ['control', 'answer', 'detail', 'all', 'any', 'not'] as const;
export const MANUAL_KINDS = new Set<string>(['declaration', 'broker', 'not_applicable', 'unmapped']);

export const CONTEXT_LABEL: Record<string, string> = {
  company_name: 'Company name',
  website: 'Website',
  industry: 'Industry',
  contact_name: 'Contact name',
  contact_email: 'Contact email',
  contact_phone: 'Contact phone',
};

export const FIELD_TYPE_LABEL: Record<string, string> = {
  yes_no: 'Yes / no',
  choice: 'One choice',
  multi_choice: 'Several choices',
  number: 'Number',
  percentage: 'Percentage',
  currency: 'Amount (RM)',
  text: 'Text',
  date: 'Date',
  table: 'Table',
};

export const EFFECT_LABEL: Record<string, string> = {
  refer: 'Refers it to the insurer’s underwriters',
  decline: 'No quote',
  terms: 'Likely narrower cover',
};

/** A new test or source of this kind, keeping the question or controls the old one named where it can. */
export function blankTest(kind: (typeof TEST_KINDS)[number], from?: FormTestInput): FormTestInput {
  const question = from && 'question' in from ? from.question : '';
  if (kind === 'control') return { from: 'control', controls: from?.from === 'control' ? from.controls : [] };
  if (kind === 'answer') return { from: 'answer', question, op: 'eq', value: 'yes' };
  if (kind === 'detail') return { from: 'detail', key: 'industry', op: 'in', value: [] };
  if (kind === 'not') return { from: 'not', of: from ?? { from: 'control', controls: [] } };
  return { from: kind, of: from?.from === 'all' || from?.from === 'any' ? from.of : from ? [from] : [{ from: 'control', controls: [] }] }; // prettier-ignore
}

export function blankSource(kind: SourceKind, from?: SourceDraft): SourceDraft {
  if ((TEST_KINDS as readonly string[]).includes(kind)) {
    // A copied answer's question carries over to a test of that answer.
    const seed = from?.from === 'value' ? ({ from: 'answer', question: from.question, op: 'eq' } as FormTestInput) : from && (TEST_KINDS as readonly string[]).includes(from.from) ? (from as FormTestInput) : undefined; // prettier-ignore
    return blankTest(kind as (typeof TEST_KINDS)[number], seed) as SourceDraft;
  }
  if (kind === 'value') return { from: 'value', question: from && 'question' in from ? from.question : '' };
  if (kind === 'context') return { from: 'context', key: 'company_name' };
  if (kind === 'checklist') return { from: 'checklist' };
  return { from: kind } as SourceDraft;
}

export const sourcesOf = (f: Pick<FieldDraft, 'source'>): SourceDraft[] =>
  (Array.isArray(f.source) ? f.source : [f.source]) as SourceDraft[];
/** One source is stored as itself, several as a list (the library writes them that way too). */
export const sourceValue = (list: SourceDraft[]): FieldDraft['source'] =>
  (list.length === 1 ? list[0]! : list) as FieldDraft['source'];

const showValue = (v: unknown): string =>
  Array.isArray(v) ? v.map(showValue).join(' or ') : v === undefined ? '' : String(v);

/** A test in plain words, for lists. */
export function testText(t: FormTestInput): string {
  switch (t.from) {
    case 'control': {
      const c = t.controls.length ? t.controls.join(t.combine === 'any' ? ' or ' : ' and ') : '(no controls)';
      return `${c} ${t.yes_at === 0.5 ? 'at least partly' : 'fully'} in place`;
    }
    case 'answer':
      return `${t.question || '(question)'}${t.path ? `.${t.path}` : ''} ${OP_LABEL[t.op] ?? t.op} ${showValue(t.value)}`.trim(); // prettier-ignore
    case 'detail':
      return `${CONTEXT_LABEL[t.key] ?? t.key} ${OP_LABEL[t.op] ?? t.op} ${showValue(t.value)}`.trim();
    case 'not':
      return `not (${testText(t.of)})`;
    default:
      return t.of.map((x) => (x.from === 'all' || x.from === 'any' ? `(${testText(x)})` : testText(x))).join(t.from === 'all' ? ' and ' : ' or '); // prettier-ignore
  }
}

/** Where a field's answer comes from, in plain words. */
export function sourceText(s: SourceDraft): string {
  switch (s.from) {
    case 'value':
      return `${s.question || '(question)'}${s.path ? `.${s.path}` : ''}${s.map ? ' (mapped)' : ''}`;
    case 'context':
      return CONTEXT_LABEL[s.key] ?? s.key;
    case 'checklist':
      return 'Each option’s test';
    case 'declaration':
      return 'Director declares';
    case 'broker':
      return 'Broker fills';
    case 'not_applicable':
      return 'Doesn’t apply';
    case 'unmapped':
      return 'Not mapped';
    default:
      return testText(s as FormTestInput);
  }
}

export const fieldSourceText = (f: FieldDraft) => sourcesOf(f).map(sourceText).join(', then ');

/** The PDF widget kinds, as the editor offers them. */
export const PDF_KINDS = [
  ['', 'Not on the PDF'],
  ['text', 'A text box'],
  ['lines', 'Text across several boxes'],
  ['radio', 'A radio group'],
  ['boxes', 'A box for each value'],
  ['table', 'A table, row by row'],
  ['columns', 'A table, a column to a box'],
] as const;
export type PdfKind = (typeof PDF_KINDS)[number][0];

export function pdfKind(t: PdfTarget | undefined): PdfKind {
  if (t === undefined) return '';
  if (typeof t === 'string') return 'text';
  if ('lines' in t) return 'lines';
  if ('radio' in t) return 'radio';
  if ('boxes' in t) return 'boxes';
  if ('columns' in t) return 'columns';
  return 'table';
}

/** A new PDF target of this kind, one value per option where the field has them. */
export function blankPdf(kind: PdfKind, f: Pick<FieldDraft, 'options'>): PdfTarget | undefined {
  const values = (f.options ?? []).map((o) => o.value);
  if (kind === '') return undefined;
  if (kind === 'text') return '';
  if (kind === 'lines') return { lines: ['', ''] };
  if (kind === 'radio') return { radio: '', states: Object.fromEntries(values.map((v, i) => [v, `/${i}`])) };
  if (kind === 'boxes') return { boxes: Object.fromEntries(values.map((v) => [v, ''])) };
  if (kind === 'columns') return { columns: {} };
  return { table: { '0': {} } };
}

/**
 * Turns text typed into a value box into what the form compares: a number when it is one, true or false,
 * else the text itself.
 */
export function parseScalar(s: string): Scalar {
  const t = s.trim();
  if (t === 'true') return true;
  if (t === 'false') return false;
  if (t !== '' && /^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return t;
}
export const parseScalars = (s: string): Scalar[] => s.split(',').map((x) => x.trim()).filter(Boolean).map(parseScalar); // prettier-ignore

/** Every field of the form, with the section it is in. */
export function allFields(form: FormDraft): { section: number; index: number; field: FieldDraft }[] {
  return form.sections.flatMap((s, section) => s.fields.map((field, index) => ({ section, index, field })));
}

export interface FormIssue extends Issue {
  /** The section and field it is about, to open them. */
  at?: { section: number; field?: number };
}

/** What stops the form being saved: its shape, as the core checks it. */
export function formIssues(form: FormDraft): FormIssue[] {
  const r = InsurerFormDefinition.safeParse(form);
  if (r.success) return [];
  return r.error.issues.map((i) => {
    const [head, s, , f] = i.path;
    const at = head === 'sections' && typeof s === 'number' ? { section: s, ...(typeof f === 'number' ? { field: f } : {}) } : undefined; // prettier-ignore
    return { path: i.path.join('.'), message: plainMessage(i.message), ...(at ? { at } : {}) };
  });
}

/** Zod's wording for the common cases, in the studio's. */
function plainMessage(m: string): string {
  if (/^Too small: expected string to have >=1 characters?$/.test(m)) return 'can’t be empty';
  if (/^Too big: expected string to have <=(\d+) characters?$/.test(m))
    return m.replace(/^Too big: expected string to have <=(\d+) characters?$/, 'at most $1 characters');
  if (/^Invalid string: must match pattern/.test(m)) return 'lowercase letters, digits and _';
  return m;
}

/** A field's parts, as the editor names them. */
const FIELD_PART: Record<string, string> = { key: 'Key', ref: 'Insurer’s number', label: 'Label', type: 'Type', options: 'Options', source: 'Where the answer comes from', if: 'Asked only if', na_when: 'Doesn’t apply when', blocks: 'Stops or changes a quote' }; // prettier-ignore

const TOP: Record<string, string> = { insurer: 'Insurer', product: 'Product', form_code: 'Form code', country: 'Country', description: 'Description', source_document: 'Blank form', sections: 'Sections', eligibility: 'Who it is for', rating: 'Premium table', pdf: 'The insurer’s PDF' }; // prettier-ignore

/** An issue's place in plain words: "B. Applicant’s details › legal_name", or the form-level part. */
export function issueLabel(form: FormDraft, issue: FormIssue): string {
  if (issue.at) {
    const s = form.sections[issue.at.section];
    const f = issue.at.field === undefined ? undefined : s?.fields[issue.at.field];
    const part = issue.path.split('.')[issue.at.field === undefined ? 2 : 4];
    const name = issue.at.field === undefined ? (part === 'applies' ? 'Only for some businesses' : part === 'key' ? 'Key' : part === 'title' ? 'Title' : null) : (FIELD_PART[part ?? ''] ?? null); // prettier-ignore
    return [s?.title || s?.key || `Section ${issue.at.section + 1}`, f ? f.key || `field ${issue.at.field! + 1}` : null, name].filter(Boolean).join(' › '); // prettier-ignore
  }
  const [head, , key] = issue.path.split('.');
  if (head === 'pdf' && key) return `The insurer’s PDF › ${key}`;
  return TOP[head ?? ''] ?? (issue.path || 'Form');
}

/** A new field's key: the label in lowercase words joined by _, unique in the form. */
export function fieldKeyFor(label: string, taken: ReadonlySet<string>): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'field'; // prettier-ignore
  const start = /^[a-z0-9]/.test(base) ? base : `f_${base}`;
  let key = start;
  for (let n = 2; taken.has(key); n++) key = `${start}_${n}`;
  return key;
}

/** Moves an item of a list up or down. */
export function move<T>(list: readonly T[], i: number, by: -1 | 1): T[] {
  const j = i + by;
  if (j < 0 || j >= list.length) return [...list];
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/** Renames a field everywhere the form names it: other fields' "only if", eligibility, the PDF map. */
export function renameField(form: FormDraft, from: string, to: string): FormDraft {
  if (from === to) return form;
  const pdfFields = form.pdf ? Object.fromEntries(Object.entries(form.pdf.fields).map(([k, t]) => [k === from ? to : k, t])) : undefined; // prettier-ignore
  return {
    ...form,
    sections: form.sections.map((s) => ({
      ...s,
      fields: s.fields.map((f) => ({
        ...f,
        key: f.key === from ? to : f.key,
        ...(f.if?.field === from ? { if: { ...f.if, field: to } } : {}),
      })),
    })),
    ...(form.eligibility ? { eligibility: { ...form.eligibility, fields: (form.eligibility.fields ?? []).map((k) => (k === from ? to : k)) } } : {}), // prettier-ignore
    ...(form.pdf && pdfFields ? { pdf: { ...form.pdf, fields: pdfFields } } : {}),
  };
}

/** Removes a field and what names it: its PDF box, eligibility, and other fields' "only if" on it. */
export function removeField(form: FormDraft, section: number, index: number): FormDraft {
  const key = form.sections[section]!.fields[index]!.key;
  const pdfFields = form.pdf ? Object.fromEntries(Object.entries(form.pdf.fields).filter(([k]) => k !== key)) : undefined; // prettier-ignore
  return {
    ...form,
    sections: form.sections.map((s, i) => ({
      ...s,
      fields: (i === section ? s.fields.filter((_, j) => j !== index) : s.fields).map((f) => {
        if (f.if?.field !== key) return f;
        const rest = { ...f };
        delete rest.if;
        return rest;
      }),
    })),
    ...(form.eligibility ? { eligibility: { ...form.eligibility, fields: (form.eligibility.fields ?? []).filter((k) => k !== key) } } : {}), // prettier-ignore
    ...(form.pdf && pdfFields ? { pdf: { ...form.pdf, fields: pdfFields } } : {}),
  };
}

/** A new field: text the broker fills until it is mapped. */
export const newField = (taken: ReadonlySet<string>): FieldDraft => ({
  key: fieldKeyFor('new field', taken),
  ref: '',
  label: 'New field',
  type: 'text',
  source: { from: 'unmapped' },
});

/** The options a type starts with, keeping the ones it had where they still fit. */
export function optionsFor(type: string, current: FieldDraft['options']): FieldDraft['options'] {
  if (type === 'yes_no') {
    const has = (v: string) => (current ?? []).some((o) => o.value === v);
    return has('yes') && has('no')
      ? current
      : [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ];
  }
  if (type === 'choice' || type === 'multi_choice') return current?.length ? current : [{ value: 'option_1', label: 'Option 1' }]; // prettier-ignore
  return [];
}
